/**
 * When each cart line arrives at the shopper's ship-to location, under the
 * line itself — as checkout will say it for the shipment the line goes in.
 *
 *   under each line   its shipment's preselected rate ("Free Delivery Oct 27 –
 *                     29", "Free Shipping · 3 to 8 business days"), or
 *                     "Doesn't ship to Alaska" / a Shipping Rules block message;
 *                     a line with merchant shipping text keeps that text (no
 *                     slot is rendered for it — snippets/cart-products.liquid)
 *   cart summary      "Delivery to 07030 · Change" (snippets/cart-delivery.liquid,
 *                     which also inlines the data)
 *
 * Shipments, options and dates come from assets/delivery-core.js resolveCart
 * — checked against 30 of 30 prod checkouts (2026-10-20). A Collective
 * carrier shipment (or one with a hide rule) is re-asked of checkout's own
 * engine through OM's live check with exactly its lines
 * (/public/delivery-options?lines=…), only once the block is on screen and
 * once per shipment, ZIP and day — Shopify rations that path, so it is never
 * prefetched; until it answers, the snapshot stands.
 *
 * Re-renders on: load, a re-rendered cart (the drawer and the cart page swap
 * their markup on every change — a MutationObserver picks the new block up),
 * `birch:ship-to-changed`, and a tab coming back into view.
 */
(function () {
  'use strict';

  var D = window.BirchDelivery;
  if (!D || window.__birchCartDelivery) return;
  window.__birchCartDelivery = true;

  var SELECTOR = '[data-cart-delivery]';
  // Either the summary or the lines may be re-rendered on their own.
  var WATCHED = '[data-cart-delivery], [data-cart-line-delivery]';
  var PROD_OM = 'https://order-management.api.birchstore.com/public/delivery-options';
  var STAGING_OM = 'https://order-management.api-staging.birchstore.com/public/delivery-options';
  var NON_PROD_HOST_RX = /(^|\.)birch-staging\.myshopify\.com$|^(localhost|127\.0\.0\.1|.*\.local)$/i;
  var LIVE_KEY = 'birch:cart-delivery-live';
  var LIVE_MAX = 30;
  var LIVE_TIMEOUT_MS = 10000;

  function liveUrl() {
    if (window.Theme && window.Theme.deliveryOptionsUrl) return window.Theme.deliveryOptionsUrl;
    return NON_PROD_HOST_RX.test(window.location.hostname) ? STAGING_OM : PROD_OM;
  }

  function shipTo() {
    return window.BirchShipTo ? window.BirchShipTo.get() : null;
  }

  function readData(block) {
    var el = block.querySelector('[data-cart-delivery-data]');
    if (!el) return null;
    if (el.__birchText !== el.textContent) {
      el.__birchText = el.textContent;
      try {
        el.__birchData = JSON.parse(el.textContent);
      } catch (_err) {
        el.__birchData = null;
      }
    }
    return el.__birchData;
  }

  /** The inlined data → resolveCart's input lines (plans and rules looked up in the mirror). */
  function cartInput(d, live) {
    var mirror = d.mirror;
    var lines = (d.lines || []).map(function (l) {
      var rules = {};
      (l.rules || []).forEach(function (k) {
        if (mirror.rules && mirror.rules[k]) rules[k] = mirror.rules[k];
      });
      var planKey = mirror.plans && mirror.plans[l.planKey] ? l.planKey : mirror.defaultPlan;
      return {
        key: l.key,
        variant: l.variant,
        quantity: l.quantity,
        price: l.price,
        planKey: planKey,
        plan: mirror.plans ? mirror.plans[planKey] : null,
        origin: l.origin,
        from: l.from,
        at: l.at,
        vendor: l.vendor,
        snapshot: l.snapshot,
        productSnapshot: l.productSnapshot || null,
        rules: rules,
        policy: l.policy,
        title: l.title,
        ships: l.ships
      };
    });
    return { mirror: mirror, lines: lines, live: live };
  }

  // ── Live check, one carrier shipment at a time ─────────────────────────────

  var memo = {};
  var inflight = {};

  function session() {
    try {
      return JSON.parse(window.sessionStorage.getItem(LIVE_KEY)) || {};
    } catch (_err) {
      return {};
    }
  }

  function remember(key, value) {
    memo[key] = value;
    try {
      var all = session();
      all[key] = value;
      var keys = Object.keys(all);
      while (keys.length > LIVE_MAX) delete all[keys.shift()];
      window.sessionStorage.setItem(LIVE_KEY, JSON.stringify(all));
    } catch (_err) {
      /* memory only */
    }
  }

  function linesParam(lines) {
    var qty = {};
    lines.forEach(function (l) {
      qty[l.variant] = (qty[l.variant] || 0) + (Number(l.quantity) || 1);
    });
    return Object.keys(qty).sort().map(function (v) { return v + ':' + qty[v]; }).join(',');
  }

  function liveKey(param, loc, day) {
    return param + '|' + loc.zip + '|' + (loc.po ? 1 : 0) + '|' + day;
  }

  /** Known live answers for this cart's carrier shipments, by shipmentKey. */
  function liveFor(input, loc, day) {
    var out = {};
    if (!loc || !loc.zip) return out;
    var stored = session();
    groupsOf(input.lines).forEach(function (g) {
      var k = liveKey(linesParam(g.lines), loc, day);
      var hit = Object.prototype.hasOwnProperty.call(memo, k) ? memo[k] : stored[k];
      if (hit && Array.isArray(hit.options)) out[g.key] = hit;
    });
    return out;
  }

  function groupsOf(lines) {
    var order = [];
    var by = {};
    lines.forEach(function (l) {
      if (l.ships === false || !l.plan) return;
      var k = D.shipmentKey(l);
      if (!by[k]) {
        by[k] = { key: k, lines: [], worth: false };
        order.push(by[k]);
      }
      by[k].lines.push(l);
      if (l.plan.carrier || D.hasHideRule(l.rules)) by[k].worth = true;
    });
    return order;
  }

  function askLive(input, loc, day, done) {
    if (!loc || !loc.zip || !window.fetch) return;
    groupsOf(input.lines).forEach(function (g) {
      if (!g.worth || g.lines.length > 20) return;
      var param = linesParam(g.lines);
      var k = liveKey(param, loc, day);
      if (Object.prototype.hasOwnProperty.call(memo, k) || session()[k] || inflight[k]) return;
      inflight[k] = true;
      var ctrl = window.AbortController ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, LIVE_TIMEOUT_MS) : null;
      var url = liveUrl() + '?lines=' + encodeURIComponent(param) + '&zip=' + encodeURIComponent(loc.zip) + '&po=' + (loc.po ? 1 : 0);
      fetch(url, { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (a) {
          if (a && a.ok) {
            remember(k, { day: a.day, options: a.options || [], blocked: a.blocked || null });
            done();
          } else {
            memo[k] = false; // keep the snapshot for this page; ask again on the next
          }
        })
        .catch(function () {
          memo[k] = false;
        })
        .then(function () {
          if (timer) clearTimeout(timer);
          delete inflight[k];
        });
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /** Every per-line slot of the cart this block summarises (drawer or cart page). */
  function slotsFor(block) {
    var host = block.closest('cart-drawer-component, .section') || document;
    return host.querySelectorAll('[data-cart-line-delivery]');
  }

  function header(loc) {
    var p = el('p', 'cart-delivery__to');
    if (loc && loc.state) {
      p.appendChild(document.createTextNode('Delivery to '));
      p.appendChild(el('strong', null, loc.zip || D.stateName(loc.state)));
      p.appendChild(document.createTextNode(' · '));
    } else {
      p.appendChild(document.createTextNode('Enter your ZIP code for delivery dates · '));
    }
    var change = el('button', 'cart-delivery__change', loc && loc.state ? 'Change' : 'Set location');
    change.type = 'button';
    change.setAttribute('data-ship-to-open', '');
    change.setAttribute('aria-haspopup', 'dialog');
    p.appendChild(change);
    return p;
  }

  function render(block) {
    var d = readData(block);
    var body = block.querySelector('[data-cart-delivery-body]');
    if (!d || !d.mirror || !body) return;
    var loc = shipTo();
    var now = Date.now();
    var day = D.zonedNow(now, d.mirror.timeZone).day;
    var base = cartInput(d, null);
    var input = cartInput(d, liveFor(base, loc, day));
    var view = loc ? D.resolveCart(input, loc, now) : null;

    // Each line's text: its shipment's line, or why it doesn't ship.
    var byLine = {};
    if (view) {
      var gone = {};
      view.unshippable.forEach(function (k) { gone[k] = true; });
      view.shipments.forEach(function (s) {
        var v = s.view;
        if (!v) return;
        var text = v.blocked ? v.blocked : v.ships ? v.line : "Doesn't ship to " + D.stateName(view.state);
        var warn = !!v.blocked || !v.ships;
        s.lines.forEach(function (k) { byLine[k] = { text: gone[k] ? "Doesn't ship to " + D.stateName(view.state) : text, warn: warn || !!gone[k] }; });
      });
    }
    var slots = slotsFor(block);
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      var hit = byLine[slot.getAttribute('data-cart-line-delivery')];
      if (hit) {
        if (slot.textContent !== hit.text) slot.textContent = hit.text;
        slot.classList.toggle('cart-line-delivery--warn', hit.warn);
        slot.hidden = false;
      } else {
        slot.hidden = true;
      }
    }

    var frag = document.createDocumentFragment();
    frag.appendChild(header(loc));
    if (body.replaceChildren) body.replaceChildren(frag);
    else {
      body.textContent = '';
      body.appendChild(frag);
    }
    block.hidden = false;
    watch(block, function () {
      askLive(base, loc, day, renderAll);
    });
  }

  // Ask the live check only once a block is on screen (the drawer opened, the
  // cart page scrolled to it).
  var seen = window.IntersectionObserver
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.target.__birchOnSeen) {
            var fn = e.target.__birchOnSeen;
            e.target.__birchOnSeen = null;
            seen.unobserve(e.target);
            fn();
          }
        });
      })
    : null;

  function watch(block, fn) {
    if (!seen) return fn();
    block.__birchOnSeen = fn;
    seen.observe(block);
  }

  function renderAll() {
    var blocks = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < blocks.length; i++) render(blocks[i]);
  }

  renderAll();
  document.addEventListener('birch:ship-to-changed', renderAll);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') renderAll();
  });

  // The drawer and the cart page re-render their markup on every change.
  var queued = false;
  new MutationObserver(function (mutations) {
    if (queued) return;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1 && (n.matches(WATCHED) || n.querySelector(WATCHED))) {
          queued = true;
          requestAnimationFrame(function () {
            queued = false;
            renderAll();
          });
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
